/**
 * Embroidery Component
 *
 * Yes/No embroidery upsell for builder frames whose eligibility depends on one
 * frame option (Backing → Linen, Layout → Two Prints). The shopper keeps
 * choosing the ordinary swatch; on "Yes" this component:
 *   - resolves the fulfilment frame variant (same options, eligibility value
 *     swapped for e.g. "Linen Embroidery") and posts it via
 *     data-cart-variant-override on the main variant input (see
 *     AddToCartForm.submit in global.js) — the displayed variant never changes;
 *   - activates the linked add-on bundle line whose variant is the chosen
 *     Font / Thread Color combination and carries the surcharge;
 *   - posts Line One / Line Two / Font / Thread Color as parent-line properties.
 *
 * Every payload input starts with data-name only; syncPayload() copies it to
 * name while the value should be posted, so "No" and ineligible states leave no
 * embroidery fields in the request and the bundle position count stays exact.
 */

class EmbroideryComponent extends HTMLElement {
  constructor() {
    super();
    this.isInitializing = true;
    this.builder = this.closest('product-main-builder');
    this.section = this.closest('.embroidery-section');
    this.addonData = JSON.parse(this.querySelector('[data-embroidery-addon-json]').textContent);
    this.addonVariantInput = this.querySelector('.embroidery-addon-variant');
    // Add-on bundle line inputs: every data-name that is not a parent-line (items[0]) property.
    this.addonInputs = Array.from(this.querySelectorAll('[data-name]')).filter((input)=>{
      var name = input.dataset.name || '';
      return name.indexOf('items[') === 0 && name.indexOf('items[0][properties[Embroidery') !== 0;
    });
    this.embroideryFlagInput = this.querySelector('[data-name="items[0][properties[Embroidery]]"]');
    this.choiceRadios = Array.from(this.querySelectorAll('.embroidery-choice'));
    this.fields = this.querySelector('[data-embroidery-fields]');
    this.lineOne = this.querySelector('[data-embroidery-line="one"]');
    this.lineTwo = this.querySelector('[data-embroidery-line="two"]');
    this.optionGroups = Array.from(this.querySelectorAll('.embroidery-addon-option'));
    this.notes = {
      eligible: this.querySelector('[data-embroidery-note-eligible]'),
      ineligible: this.querySelector('[data-embroidery-note-ineligible]'),
      pending: this.querySelector('[data-embroidery-note-pending]')
    };
    this.note = this.querySelector('.embroidery-note');
    this.choiceGroup = this.querySelector('.embroidery-options');
    this.unavailableNote = this.querySelector('[data-embroidery-unavailable]');
    this.priceLabel = this.querySelector('[data-embroidery-price]');

    this.eligibilityOption = this.dataset.eligibilityOption;
    this.eligibleValue = this.dataset.eligibleValue;
    this.embroideryValue = this.dataset.embroideryValue;
    this.lineTwoOptional = this.dataset.lineTwoOptional === 'true';
    this.maxLength = parseInt(this.dataset.maxLength, 10) || 30;

    this.state = 'pending';
    this.selection = null;
    this.frameVariant = null;
    this.addonVariant = null;

    this.bindEvents();
    this.evaluate();
    this.isInitializing = false;
  }

  bindEvents() {
    this.choiceRadios.forEach((radio)=>{
      radio.addEventListener('change', ()=>{
        this.selection = radio.value;
        this.applySelection();
        this.notifyBuilder();
        this.refreshGallery();
      });
    });
    this.querySelectorAll('.embroidery-option-choice').forEach((radio)=>{
      radio.addEventListener('change', ()=>{
        this.resolve();
        this.notifyBuilder();
        this.refreshGallery();
      });
    });
    [this.lineOne, this.lineTwo].forEach((input)=>{
      if(!input) return;
      input.addEventListener('input', ()=>{
        this.syncPayload();
        this.builder.dispatchEvent(new CustomEvent('stepChange'));
      });
      input.addEventListener('blur', ()=>{
        input.value = input.value.trim();
        this.syncPayload();
        this.builder.dispatchEvent(new CustomEvent('stepChange'));
      });
    });
    // The gallery refilters itself on main variant changes; galleryFilter() derives
    // eligibility from the variant it is given, so no extra gallery refresh is needed here.
    this.builder.addEventListener('mainVariantChange', ()=>this.evaluate());
  }

  get variantsElement() {
    return this.builder.variantsElement || this.builder.querySelector('product-variants');
  }

  get mainVariantInput() {
    var variants = this.variantsElement;
    return variants ? variants.querySelector('[data-variant-id]') : null;
  }

  currentVariant() {
    var variant = this.builder.getCurrentVariant ? this.builder.getCurrentVariant() : null;
    return variant || null;
  }

  isPlaceholderVariant(variant) {
    return !variant || /^9{6,}/.test(String(variant.id || ''));
  }

  eligibilityOptionIndex() {
    return this.builder.productData.options.indexOf(this.eligibilityOption);
  }

  eligibilityOptionChosen(index) {
    var variants = this.variantsElement;
    if(!variants) return true;
    if(!variants.deferredSelection) return true;
    return Object.prototype.hasOwnProperty.call(variants.selectedOptions || {}, index);
  }

  /** Eligibility of a displayed variant: 'eligible', 'ineligible' or 'pending' (option not chosen yet). */
  stateFor(variant) {
    var index = this.eligibilityOptionIndex();
    if(index < 0) return 'ineligible';
    if(!variant || !this.eligibilityOptionChosen(index)) return 'pending';
    var value = variant.options ? variant.options[index] : variant['option' + (index + 1)];
    return (value === this.eligibleValue || value === this.embroideryValue) ? 'eligible' : 'ineligible';
  }

  /** Recompute eligibility from the displayed variant, then re-apply the selection. */
  evaluate() {
    var nextState = this.stateFor(this.currentVariant());

    var stateChanged = nextState !== this.state;
    this.state = nextState;

    if(this.state === 'eligible') {
      if(stateChanged) this.selection = null;
    } else if(this.state === 'ineligible') {
      this.selection = 'No';
    } else {
      this.selection = null;
    }

    this.render();
    this.applySelection();
    if(stateChanged) this.notifyBuilder();
  }

  render() {
    var eligible = this.state === 'eligible';
    var pending = this.state === 'pending';

    this.choiceRadios.forEach((radio)=>{
      radio.disabled = !eligible;
      radio.checked = this.selection !== null && radio.value === this.selection;
    });

    Object.keys(this.notes).forEach((key)=>{
      if(this.notes[key]) this.notes[key].hidden = key !== this.state;
    });

    if(this.note) {
      this.note.hidden = eligible;
      if(this.choiceGroup) {
        if(eligible) this.choiceGroup.removeAttribute('aria-describedby');
        else this.choiceGroup.setAttribute('aria-describedby', this.note.id);
      }
    }

    this.classList.toggle('is-eligible', eligible);
    this.classList.toggle('is-ineligible', this.state === 'ineligible');
    this.classList.toggle('is-pending', pending);

    // While the controlling option is unchosen the shopper cannot act here, so the
    // step must not count as "missing" in progress/validation.
    if(this.section) {
      if(pending) {
        this.section.dataset.notRequired = 'true';
      } else {
        delete this.section.dataset.notRequired;
      }
    }
  }

  applySelection() {
    var showFields = this.state === 'eligible' && this.selection === 'Yes';
    if(this.fields) this.fields.hidden = !showFields;
    // Hidden text fields must not keep the step "incomplete" in the progress bar; the
    // builder reads data-required per input when it gathers step values.
    if(this.lineOne) this.lineOne.dataset.required = showFields ? 'true' : 'false';
    if(this.lineTwo) this.lineTwo.dataset.required = (showFields && !this.lineTwoOptional) ? 'true' : 'false';
    this.resolve();
  }

  selectedAddonOptions() {
    return this.optionGroups.map((group)=>{
      var checked = group.querySelector('.embroidery-option-choice:checked');
      return checked ? checked.value : null;
    });
  }

  resolveFrameVariant(variant, index) {
    if(this.isPlaceholderVariant(variant) || index < 0 || !variant.options) return null;
    var target = variant.options.slice();
    target[index] = this.embroideryValue;
    return this.builder.productData.variants.find((candidate)=>{
      if(this.isPlaceholderVariant(candidate) || !candidate.options) return false;
      return candidate.options.length === target.length && candidate.options.every((value, i)=>value === target[i]);
    }) || null;
  }

  resolveAddonVariant(selected) {
    if(!this.addonData.ready) return null;
    if(selected.some((value)=>!value)) return null;
    return this.addonData.variants.find((candidate)=>{
      return selected.every((value, i)=>candidate.options[i] === value);
    }) || null;
  }

  /** Map the current selection to cart identities and refresh payload/price/messages. */
  resolve() {
    var active = this.state === 'eligible' && this.selection === 'Yes';
    var variant = this.currentVariant();
    var index = this.eligibilityOptionIndex();
    var selected = this.selectedAddonOptions();

    this.frameVariant = active ? this.resolveFrameVariant(variant, index) : null;
    this.addonVariant = active ? this.resolveAddonVariant(selected) : null;
    this.frameMappingMissing = active && !this.isPlaceholderVariant(variant) && !this.frameVariant;
    this.frameUnavailable = active && this.frameVariant && this.frameVariant.available === false;
    this.addonUnavailable = active && this.addonData.ready && selected.every(Boolean) && (!this.addonVariant || this.addonVariant.available === false);

    var mainInput = this.mainVariantInput;
    if(mainInput) {
      if(active && this.frameVariant && !this.frameUnavailable) {
        mainInput.dataset.cartVariantOverride = String(this.frameVariant.id);
      } else if(mainInput.dataset.cartVariantOverride !== undefined) {
        delete mainInput.dataset.cartVariantOverride;
      }
    }

    if(this.addonVariantInput) {
      this.addonVariantInput.value = this.addonVariant ? String(this.addonVariant.id) : '';
    }

    if(active && this.addonVariant && this.addonVariant.available !== false) {
      this.setAttribute('data-price', this.addonVariant.price);
    } else {
      this.removeAttribute('data-price');
    }

    if(this.unavailableNote) {
      this.unavailableNote.hidden = !(this.frameMappingMissing || this.frameUnavailable || this.addonUnavailable);
    }

    this.updatePriceLabel();
    this.syncPayload(selected);
  }

  updatePriceLabel() {
    if(!this.priceLabel) return;
    var price = this.addonVariant ? this.addonVariant.price : this.addonData.price_min;
    if(!price) {
      this.priceLabel.textContent = '';
      return;
    }
    this.priceLabel.textContent = '+' + $s.formatMoney(price).replace('.00', '');
  }

  setPosted(input, posted) {
    if(!input) return;
    if(posted) {
      input.setAttribute('name', input.dataset.name);
    } else {
      input.removeAttribute('name');
    }
  }

  /** Copy data-name → name for exactly the inputs whose values belong in the request. */
  syncPayload(selected) {
    var active = this.state === 'eligible' && this.selection === 'Yes';
    var addonPosted = active && Boolean(this.addonVariant) && this.addonVariant.available !== false;
    selected = selected || this.selectedAddonOptions();

    this.addonInputs.forEach((input)=>this.setPosted(input, addonPosted));
    this.setPosted(this.embroideryFlagInput, active);
    this.setPosted(this.lineOne, active);
    this.setPosted(this.lineTwo, active && this.lineTwo && this.lineTwo.value.trim() !== '');

    this.optionGroups.forEach((group, i)=>{
      var property = group.querySelector('[data-embroidery-option-property]');
      if(!property) return;
      property.value = selected[i] || '';
      this.setPosted(property, active && Boolean(selected[i]));
    });
  }

  /**
   * Gallery rules for embroidered photos, built once per ProductMedia.filterMedia() pass.
   * A photo is embroidered when its alt tags include "Embroidery:Yes" or an add-on option
   * (Font Style, Thread Color…). The returned function answers true/false for those photos
   * and null for every other photo, which the gallery's existing matcher then decides.
   * Tags describe the displayed selection, never the fulfilment override; keys and values
   * ignore case and surrounding/repeated whitespace (the catalog option is "Thread Color ").
   *   - Not embroidering: embroidered photos are hidden.
   *   - Yes: show the first compatible embroidered photo until customization starts.
   *     Chosen tags must match ("*" and comma lists allowed); unchosen options and unknown keys don't
   *     constrain, as in the existing matcher. A matching embroidered frame photo (tagged
   *     with the eligibility option) replaces the plain photos tagged with that option;
   *     without one the plain photos stay, so choosing Yes never empties the gallery.
   */
  galleryFilter(mediaList, variant) {
    const normalize = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const parseTags = alt => String(alt || '').split(';').filter(tag => tag.includes(':')).map(tag => {
      const separator = tag.indexOf(':');
      return {key: normalize(tag.slice(0, separator)), values: tag.slice(separator + 1).split(',').map(normalize)};
    });
    // Derived from the variant being filtered: the gallery can run before evaluate() on a main variant change.
    const active = this.selection === 'Yes' && this.stateFor(variant) === 'eligible';
    const optionKeys = this.optionGroups.map(group => normalize(group.dataset.optionName));
    const eligibilityKey = normalize(this.eligibilityOption);
    const isEmbroidered = tags => tags.some(tag => optionKeys.includes(tag.key) ||
      (tag.key === 'embroidery' && tag.values.every(value => value === 'yes')));
    const hasEligibilityTag = tags => tags.some(tag => tag.key === eligibilityKey);
    const tagsById = new Map(mediaList.map(media => [String(media.id), parseTags(media.alt)]));

    const selections = new Map();
    (this.builder.productData.options || []).forEach((name, i) => {
      selections.set(normalize(name), normalize(variant?.options?.[i]));
    });
    // One call covers every tied product in the builder.
    const tiedProduct = this.builder.querySelector('tied-product');
    (tiedProduct?.collectBuilderSelectedOptionValues?.() || []).forEach(tag => {
      const separator = tag.indexOf(':');
      selections.set(normalize(tag.slice(0, separator)), normalize(tag.slice(separator + 1)));
    });
    this.selectedAddonOptions().forEach((value, i) => selections.set(optionKeys[i], normalize(value)));
    selections.set('embroidery', 'yes');

    const matchesTag = tag => {
      if(tag.values.includes('*')) return true;
      const selection = selections.get(tag.key);
      if(!selection) return true;
      return tag.values.includes(selection) || (tag.values.includes('unselected') && selection !== 'unselected');
    };
    let matched = active ? Array.from(tagsById.keys()).filter(id => {
      const tags = tagsById.get(id);
      return isEmbroidered(tags) && tags.every(matchesTag);
    }) : [];
    // An example photo is not a default font/thread choice and must not fill the form.
    if(!optionKeys.some(key => selections.get(key))) matched = matched.slice(0, 1);
    const framePhotoId = matched.find(id => hasEligibilityTag(tagsById.get(id)));

    const filter = media => {
      const id = String(media.id);
      const tags = tagsById.get(id) || parseTags(media.alt);
      if(isEmbroidered(tags)) return matched.includes(id);
      if(framePhotoId && hasEligibilityTag(tags)) return false;
      return null;
    };
    // The gallery leads with the embroidered frame photo, else any matching embroidered photo.
    filter.preferredMediaId = framePhotoId || matched[0] || null;
    return filter;
  }

  notifyBuilder() {
    if(this.isInitializing) return;
    this.builder.dispatchEvent(new CustomEvent('stepChange'));
    this.builder.dispatchEvent(new CustomEvent('bundleChange'));
  }

  /** Refilter the gallery after a Yes/No, font or thread choice; announce only a visible change. */
  refreshGallery() {
    var gallery = {changed: false};
    this.builder.dispatchEvent(new CustomEvent('embroideryGalleryChange', {detail: gallery}));
    var status = this.querySelector('[data-embroidery-gallery-status]');
    if(!gallery.changed || !status) return;
    // Clear first so a repeated, identical message is announced again.
    status.textContent = '';
    setTimeout(()=>{ status.textContent = status.dataset.message; }, 100);
  }

  /** Called by BuilderButtons.validate(); returns a message when checkout must not proceed. */
  validateForCheckout() {
    if(this.state !== 'eligible') return null;
    if(this.selection === null) {
      return 'Please let us know whether you would like embroidery.';
    }
    if(this.selection !== 'Yes') return null;

    if(this.lineOne) this.lineOne.value = this.lineOne.value.trim();
    if(this.lineTwo) this.lineTwo.value = this.lineTwo.value.trim();

    if(!this.lineOne || this.lineOne.value === '') {
      return 'Please enter the first embroidery line.';
    }
    if(!this.lineTwoOptional && (!this.lineTwo || this.lineTwo.value === '')) {
      return 'Please enter the second embroidery line.';
    }
    if(this.lineOne.value.length > this.maxLength || (this.lineTwo && this.lineTwo.value.length > this.maxLength)) {
      return 'Embroidery lines can be at most ' + this.maxLength + ' characters.';
    }

    var selected = this.selectedAddonOptions();
    var missingGroup = this.optionGroups.find((group, i)=>!selected[i]);
    if(missingGroup) {
      return 'Please choose an embroidery ' + (missingGroup.dataset.optionName || 'option').toLowerCase() + '.';
    }

    if(this.isPlaceholderVariant(this.currentVariant())) {
      return 'Please select all required frame details before checkout.';
    }
    if(!this.addonData.ready) {
      return 'Embroidery is not available for purchase yet. Please choose "No thanks" to continue.';
    }
    if(this.frameMappingMissing || this.frameUnavailable || this.addonUnavailable || !this.frameVariant || !this.addonVariant) {
      return 'Embroidery is not available for this combination yet. Please choose "No thanks" to continue.';
    }

    this.syncPayload(selected);
    return null;
  }
}

customElements.define('embroidery-component', EmbroideryComponent);
